import React, { useState } from "react";
import { adminApi } from "../../api";
import { useContent } from "../../context/ContentContext";
import { useToast } from "../../hooks/use-toast";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { CATEGORY_ICONS, categoryIcon } from "../../constants/categoryIcons";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Check, X } from "lucide-react";

function IconPicker({ value, onPick, label }) {
  const [open, setOpen] = useState(false);
  const Current = CATEGORY_ICONS[value] || null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" aria-label={label} title="Change icon" className="h-10 w-10 shrink-0 rounded-lg border border-border bg-[#f7fafc] flex items-center justify-center text-[#0e7490] hover:bg-[#eaf4f7]">
          {Current ? <Current className="h-5 w-5" /> : <span className="text-xs text-[#9aa8b5]">Icon</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="grid grid-cols-6 gap-1">
          {Object.entries(CATEGORY_ICONS).map(([name, Cmp]) => (
            <button
              key={name}
              type="button"
              title={name}
              aria-label={name}
              onClick={() => { onPick(name); setOpen(false); }}
              className={`h-9 w-9 rounded-md flex items-center justify-center hover:bg-[#eaf4f7] ${value === name ? "bg-[#eaf4f7] text-[#0e7490] ring-1 ring-[#0e7490]" : "text-[#33445a]"}`}
            >
              <Cmp className="h-4 w-4" />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Add / rename / reorder / delete product categories. Every action saves immediately. */
export default function CategoryManager({ open, onOpenChange, products, onChanged }) {
  const { toast } = useToast();
  const { content, refresh } = useContent();
  const cats = content.productCategories || [];
  const [newName, setNewName] = useState("");
  const [newIcon, setNewIcon] = useState("Pill");
  const [editing, setEditing] = useState(null); // category name being renamed
  const [editValue, setEditValue] = useState("");
  const [busy, setBusy] = useState(false);

  const countFor = (name) => products.filter((p) => p.category === name).length;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      await refresh();
      onChanged?.();
      if (okMsg) toast({ title: okMsg });
    } catch (err) {
      toast({ title: "Couldn't save", description: err?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const add = (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    run(async () => {
      await adminApi.post("/admin/categories", { name: newName, icon: newIcon });
      setNewName("");
    }, "Category added");
  };

  const rename = (old) => {
    const next = editValue.trim();
    setEditing(null);
    if (!next || next === old) return;
    run(() => adminApi.put("/admin/categories/rename", { old, new: next }), "Category renamed");
  };

  const move = (i, dir) => {
    const names = cats.map((c) => c.name);
    const j = i + dir;
    if (j < 0 || j >= names.length) return;
    [names[i], names[j]] = [names[j], names[i]];
    run(() => adminApi.put("/admin/categories/order", { names }));
  };

  const remove = (name) => run(() => adminApi.delete("/admin/categories", { params: { name } }), "Category deleted");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Product categories</DialogTitle>
          <DialogDescription>
            These appear on the home page, the footer, the Products page filters and the category list when adding a product. Changes save immediately.
          </DialogDescription>
        </DialogHeader>

        <ul className="divide-y divide-border rounded-xl border border-border">
          {cats.length === 0 && <li className="p-6 text-center text-sm text-[#9aa8b5]">No categories yet. Add the first one below.</li>}
          {cats.map((c, i) => {
            const n = countFor(c.name);
            const Ic = categoryIcon(c);
            return (
              <li key={c.name} className="p-3 flex items-center gap-2">
                <IconPicker
                  label={`Icon for ${c.name}`}
                  value={c.icon || Object.keys(CATEGORY_ICONS).find((k) => CATEGORY_ICONS[k] === Ic)}
                  onPick={(icon) => run(() => adminApi.put("/admin/categories/rename", { old: c.name, new: c.name, icon }))}
                />
                {editing === c.name ? (
                  <form className="flex-1 flex items-center gap-1.5" onSubmit={(e) => { e.preventDefault(); rename(c.name); }}>
                    <Input autoFocus value={editValue} onChange={(e) => setEditValue(e.target.value)} className="h-10" />
                    <button type="submit" aria-label="Save name" className="h-10 w-10 rounded-lg border border-border flex items-center justify-center text-emerald-600 hover:bg-emerald-50"><Check className="h-4 w-4" /></button>
                    <button type="button" aria-label="Cancel" onClick={() => setEditing(null)} className="h-10 w-10 rounded-lg border border-border flex items-center justify-center hover:bg-secondary"><X className="h-4 w-4" /></button>
                  </form>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-[#0f1e33] truncate">{c.name}</p>
                      <p className="text-xs text-[#9aa8b5]">{n} {n === 1 ? "product" : "products"}</p>
                    </div>
                    <button disabled={busy || i === 0} onClick={() => move(i, -1)} aria-label="Move up" className="h-9 w-9 rounded-lg border border-border flex items-center justify-center disabled:opacity-30 hover:bg-secondary"><ArrowUp className="h-4 w-4" /></button>
                    <button disabled={busy || i === cats.length - 1} onClick={() => move(i, 1)} aria-label="Move down" className="h-9 w-9 rounded-lg border border-border flex items-center justify-center disabled:opacity-30 hover:bg-secondary"><ArrowDown className="h-4 w-4" /></button>
                    <button disabled={busy} onClick={() => { setEditing(c.name); setEditValue(c.name); }} aria-label="Rename" className="h-9 w-9 rounded-lg border border-border flex items-center justify-center text-[#0e7490] hover:bg-[#eaf4f7]"><Pencil className="h-4 w-4" /></button>
                    <button
                      disabled={busy || n > 0}
                      onClick={() => remove(c.name)}
                      aria-label="Delete"
                      title={n > 0 ? "Move or delete this category's products first" : "Delete category"}
                      className="h-9 w-9 rounded-lg border border-border flex items-center justify-center text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:hover:bg-transparent"
                    ><Trash2 className="h-4 w-4" /></button>
                  </>
                )}
              </li>
            );
          })}
        </ul>

        <form onSubmit={add} className="flex items-center gap-2">
          <IconPicker label="Icon for the new category" value={newIcon} onPick={setNewIcon} />
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New category name" className="h-10" />
          <Button type="submit" disabled={busy || !newName.trim()} className="bg-[#0e7490] hover:bg-[#0b5563] text-white h-10 shrink-0">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </form>
        <p className="text-xs text-[#9aa8b5]">A category can only be deleted once it has no products.</p>
      </DialogContent>
    </Dialog>
  );
}
